import { AssociationType } from "@/shared/domain/association";
import { FileRecord } from "@/shared/domain/files";
import { TopicRecord } from "@/shared/domain/topics";

import type { Session, SessionClipRange } from "./EphemeralWorkspaceState";

/**
 * One recording session's worth of workspace data: the unit a plot panel
 * aligns when overlaying the same signal from multiple sessions. A session
 * loaded into the workspace becomes one source holding every file it lists; a
 * file that belongs to no session is its own source.
 *
 * `startTime`/`endTime` run from the earliest start to the latest end over the
 * member files, each file's own span first narrowed by the session's clip range
 * for that file when one is declared.
 */
export interface WorkspaceSource {
  sourceId: string;
  fileIds: string[];
  startTime: bigint;
  endTime: bigint;
}

/**
 * The time span a file adds to its source: earliest topic start to latest topic
 * end, narrowed to `clipRange` when the session declares one. `fileTopics` must
 * hold only topics associated with this file. Undefined for a file whose topics
 * carry no time bounds, or whose clip range excludes all of its data; such a
 * file joins no source.
 *
 * A topic missing either bound is skipped whole. `determineTimeBounds`
 * (`@/shared/domain/topics/determineTimeBounds`) instead folds each bound
 * independently, so there a topic with only a `start_time` still pulls the
 * start earlier. The span returned here decides where a series plots as x = 0
 * and bounds the playhead, so only topics whose full extent is known widen it.
 */
function fileTimeSpan(
  fileTopics: TopicRecord[],
  clipRange: SessionClipRange | undefined,
): { startTime: bigint; endTime: bigint } | undefined {
  let startTime: bigint | undefined;
  let endTime: bigint | undefined;
  for (const topic of fileTopics) {
    const { start_time, end_time } = topic;
    if (
      start_time === null ||
      start_time === undefined ||
      end_time === null ||
      end_time === undefined
    ) {
      continue;
    }
    if (startTime === undefined || start_time < startTime) {
      startTime = start_time;
    }
    if (endTime === undefined || end_time > endTime) {
      endTime = end_time;
    }
  }
  if (startTime === undefined || endTime === undefined) {
    return undefined;
  }

  if (clipRange !== undefined) {
    if (clipRange.min > startTime) {
      startTime = clipRange.min;
    }
    if (clipRange.max < endTime) {
      endTime = clipRange.max;
    }
  }
  // Also catches a file whose own topics invert, which takes no clip range at
  // all: one topic reporting an end_time before its start_time is enough.
  if (startTime > endTime) {
    return undefined;
  }

  return { startTime, endTime };
}

/**
 * Add `value` to the list stored at `key`, starting the list when `key` has
 * none yet.
 */
function appendToGroup<K, V>(groups: Map<K, V[]>, key: K, value: V): void {
  const group = groups.get(key);
  if (group === undefined) {
    groups.set(key, [value]);
  } else {
    group.push(value);
  }
}

/** Topics grouped by the file they are associated with. */
function groupTopicsByFileId(
  topics: TopicRecord[],
): Map<string, TopicRecord[]> {
  const topicsByFileId = new Map<string, TopicRecord[]>();
  for (const topic of topics) {
    if (topic.association.association_type !== AssociationType.File) {
      continue;
    }
    appendToGroup(topicsByFileId, topic.association.association_id, topic);
  }
  return topicsByFileId;
}

/**
 * Widen `source` to cover `span`, or start a source at `span` when this is the
 * first file to contribute one.
 */
function addFileToSource(
  source: WorkspaceSource | undefined,
  sourceId: string,
  fileId: string,
  span: { startTime: bigint; endTime: bigint },
): WorkspaceSource {
  if (source === undefined) {
    return {
      sourceId,
      fileIds: [fileId],
      startTime: span.startTime,
      endTime: span.endTime,
    };
  }

  source.fileIds.push(fileId);
  if (span.startTime < source.startTime) {
    source.startTime = span.startTime;
  }
  if (span.endTime > source.endTime) {
    source.endTime = span.endTime;
  }
  return source;
}

/**
 * The workspace's alignment sources, ordered by start time, earliest first,
 * with ties broken on source id.
 *
 * Each session yields one source covering the files it lists, and every
 * remaining file yields a source of its own. A file two sessions both list
 * counts toward each of their sources, matching how it appears under each
 * session in the Files tab. A file that has no span to add joins no source, and
 * a session whose files all drop out that way yields no source at all.
 */
export function deriveWorkspaceSources(
  files: FileRecord[],
  topics: TopicRecord[],
  sessions: Session[],
): WorkspaceSource[] {
  const topicsByFileId = groupTopicsByFileId(topics);
  const sourcesById = new Map<string, WorkspaceSource>();

  const fileIdsInASession = new Set<string>();
  for (const session of sessions) {
    const sourceId = `session:${session.id}`;
    for (const file of session.files) {
      const fileId = file.file_id;
      fileIdsInASession.add(fileId);
      const span = fileTimeSpan(
        topicsByFileId.get(fileId) ?? [],
        session.clipRangeByFileId.get(fileId),
      );
      if (span === undefined) {
        continue;
      }
      sourcesById.set(
        sourceId,
        addFileToSource(sourcesById.get(sourceId), sourceId, fileId, span),
      );
    }
  }

  for (const file of files) {
    const fileId = file.file_id;
    if (fileIdsInASession.has(fileId)) {
      continue;
    }
    const span = fileTimeSpan(topicsByFileId.get(fileId) ?? [], undefined);
    if (span === undefined) {
      continue;
    }
    const sourceId = `file:${fileId}`;
    sourcesById.set(
      sourceId,
      addFileToSource(sourcesById.get(sourceId), sourceId, fileId, span),
    );
  }

  return [...sourcesById.values()].sort((a, b) => {
    if (a.startTime !== b.startTime) {
      return a.startTime < b.startTime ? -1 : 1;
    }
    return a.sourceId.localeCompare(b.sourceId);
  });
}

/**
 * Sources grouped by each of their member file ids. A file in no source (one
 * with no topic time bounds, say) has no entry. A file two sessions both list
 * maps to both of their sources, in `sources` order, so a caller that takes
 * the first always takes the same one.
 */
export function groupSourcesByFileId(
  sources: WorkspaceSource[],
): Map<string, WorkspaceSource[]> {
  const sourcesByFileId = new Map<string, WorkspaceSource[]>();
  for (const source of sources) {
    for (const fileId of source.fileIds) {
      appendToGroup(sourcesByFileId, fileId, source);
    }
  }
  return sourcesByFileId;
}
